
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { BoPanel, IOnOffHandlers } from 'bonomo';
import { IUser } from '@/model/User/UserInterface';
import { IErrors } from '@/model/IErrors';
import { IAuthUserData } from '@/store/modules/authModule';
import { PermissionHelper } from '@/helpers/PermissionHelper';
import { EUserPermissions } from '@/model/EUserPermissions';
import { EUserRoles } from '@/model/EUserRoles';
import { IShortUser, IShortChannel } from '@/model/shortInterfaces';
import UserNav from '@/components/user/widget/UserNav.vue';
import UserSearch from '@/components/user/widget/UserSearch.vue';
import UserDeleteDialog from '@/components/user/widget/UserDeleteDialog.vue';
import LockerSearch from '@/components/locker/widget/LockerSearch.vue';

const userModule = namespace('userModule');
const authModule = namespace('authModule');
const channelModule = namespace('channelModule');
const lockerModule = namespace('lockerModule');

@Component({
  name: 'UserEdit',
  components: {
    BoPanel,
    UserNav,
    UserSearch,
    UserDeleteDialog,
    LockerSearch,
  },
})
export default class UserEdit extends Vue {
  @Prop()
  protected userId!: number;

  @userModule.Getter('user')
  protected user!: IUser;

  @userModule.Getter('errors')
  protected errors!: IErrors;

  @userModule.Action('fetchUser')
  private fetchUser!: (userId: number) => Promise<any>;

  @userModule.Action('updateUser')
  private updateUser!: (payload: { userId: number; data: any }) => Promise<any>;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  @channelModule.Getter('channels')
  private channels!: IShortChannel[];

  @channelModule.Action('fetchChannels')
  private fetchChannels!: () => void;

  @lockerModule.Action('fetchLockers')
  private fetchLockers!: () => void;

  private loading: boolean = true;
  private saving: boolean = false;
  private subscribes: IOnOffHandlers = {};

  private form: any = {};

  private rules = {
    required: (v: any) => !!v || this.$t('common.validation.required'),
    min2: (v: string) => (v && v.length >= 2) || this.$t('common.validation.min', { min: 2 }),
    email: (v: string) => !v || /.+@.+\..+/.test(v) || this.$t('common.validation.email'),
  };

  get isAdmin(): boolean {
    return PermissionHelper.hasRoleAdministrator(this.authUser);
  }

  get isSelf(): boolean {
    return '' + this.userId === '' + this.authUser.id;
  }

  get isDeveloper(): boolean {
    return PermissionHelper.hasPermission(this.authUser, EUserPermissions.PERMISSION_DEVELOPER, 0);
  }

  get canDelete(): boolean {
    if (this.isAdmin) return true;
    return PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_CHANNEL_ADMINISTRATOR);
  }

  get channelOptions(): IShortChannel[] {
    return this.channels || [];
  }

  get roleOptions() {
    return Object.values(EUserRoles).map(role => ({
      text: this.$t(`users.user.fields.role.values.${role}`),
      value: role,
    }));
  }

  get featureOptions() {
    return [
      { text: '---', value: 0 },
      { text: 'Bizcocho webapp', value: 20 },
      { text: 'GMP API', value: 1001 },
    ];
  }

  get languageOptions() {
    return [
      { text: 'Español', value: 'es' },
      { text: 'English', value: 'en' },
      { text: 'Català', value: 'ca' },
    ];
  }

  created() {
    this.subscribes = this.$ons({
      'backend.call.OK.userLoaded': this.initForm,
      'backend.call.begin.userUpdated': () => { this.saving = true; },
      'backend.call.OK.userUpdated': this.onSaveSuccess,
      'backend.call.KO.userUpdated': () => { this.saving = false; },
    });
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  loadData() {
    this.fetchUser(this.userId);
    this.fetchChannels();
    this.fetchLockers();
  }

  private initForm() {
    if (!this.user || !this.user.id) return;
    this.form = {
      first_name: this.user.first_name,
      last_name: this.user.last_name,
      phone_number: this.user.phone_number,
      email: this.user.email,
      language_code: this.user.language_code,
      active: this.user.active,
      role: this.user.role,
      nie: this.user.nie,
      company_name_for_billing: this.user.company_name_for_billing,
      invoice_payments: this.user.invoice_payments,
      invoice_emails: (this.user as any).invoice_emails || [],
      channel_id: this.user.channel_id,
      locker_id: this.user.locker_id,
      feature: this.user.feature,
      password: '',
      password_confirmation: '',
      confirm_phone_number: false,
    };
  }

  private onLockerSelected(lockerId: number) {
    this.form.locker_id = lockerId;
  }

  private onSave() {
    const data = { ...this.form };
    // Only send password if filled in
    if (!data.password) {
      delete data.password;
      delete data.password_confirmation;
    }
    this.updateUser({
      userId: this.userId,
      data,
    });
  }

  private onSaveSuccess() {
    this.saving = false;
    this.$uiBus.emit('success', this.$t('common.saved'));
    this.$router.push({ name: 'user', params: { userId: '' + this.userId } });
  }

  private onCancel() {
    this.$router.push({ name: 'user', params: { userId: '' + this.userId } });
  }

  private onDelete() {
    this.$uiBus.emit('user-delete-dialog.show');
  }

  private onGotoUser(user: IShortUser) {
    if (user.id !== this.userId) {
      this.$router.push({ name: 'userEdit', params: { userId: '' + user.id } });
      this.loadData();
    }
  }
}
